import { useEffect, useRef, useState } from "react";
import {
  Card,
  Typography,
  CardContent,
  CardActionArea,
  Grid,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  PersonOutline,
  EventAvailable,
  AccessTime,
  LocationOn,
} from "@mui/icons-material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import Colors from "../../../constants/Colors";
import { Client } from "../../../models/client/client.model";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface Props {
  toggleModal: () => void;
  selectedClient: Client | null;
  checkinDate: string;
  checkinTime: string;
  onStartVisit: (coords: GeolocationCoordinates) => void;
  isStarting: boolean;
  startCoordinates: GeolocationCoordinates | null;
  isLocked: boolean;
  isVisible: boolean;
}

export const returnApiUrl = () => localStorage.getItem("apiUrl") as string;

const VisitCheckinCard = (props: Props) => {
  const {
    toggleModal,
    selectedClient,
    checkinDate,
    checkinTime,
    onStartVisit,
    isStarting,
    startCoordinates,
    isLocked,
    isVisible,
  } = props;

  const [mapLoading, setMapLoading] = useState(true);
  const [isLocallyStarting, setIsLocallyStarting] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const zoomControlsRef = useRef<HTMLDivElement | null>(null);

  const BAD_ACCURACY_THRESHOLD = 200;

  useEffect(() => {
    if (!startCoordinates) {
      setMapLoading(true);
      return;
    }

    if (!mapContainerRef.current) {
      return;
    }

    const { latitude, longitude, accuracy } = startCoordinates;

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: false,
      }).setView([latitude, longitude], 17);

      const tileLayer = L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
      );
      tileLayer.on("load", () => setMapLoading(false));
      tileLayer.addTo(map);

      const zoomControls = document.createElement("div");
      zoomControls.style.position = "absolute";
      zoomControls.style.bottom = "15px";
      zoomControls.style.right = "15px";
      zoomControls.style.zIndex = "1000";
      zoomControls.style.display = "flex";
      zoomControls.style.flexDirection = "column";
      zoomControls.style.gap = "6px";

      const createButton = (label: string, onClick: () => void) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = label;
        button.style.background = "white";
        button.style.width = "36px";
        button.style.height = "36px";
        button.style.borderRadius = "6px";
        button.style.border = "none";
        button.style.fontSize = "18px";
        button.style.fontWeight = "bold";
        button.style.color = "#444";
        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.justifyContent = "center";
        button.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
        button.style.cursor = "pointer";
        button.onclick = onClick;
        return button;
      };

      zoomControls.appendChild(
        createButton("⌖", () => map.flyTo([latitude, longitude], 17))
      );
      zoomControls.appendChild(createButton("+", () => map.zoomIn()));
      zoomControls.appendChild(createButton("−", () => map.zoomOut()));

      mapContainerRef.current.appendChild(zoomControls);
      zoomControlsRef.current = zoomControls;
      mapInstanceRef.current = map;
    }

    const map = mapInstanceRef.current;
    map.setView([latitude, longitude], 17);

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }

    if (circleRef.current) {
      map.removeLayer(circleRef.current);
    }

    circleRef.current = L.circle([latitude, longitude], {
      radius: accuracy,
      color: "#1976d2",
      fillColor: "#1976d2",
      fillOpacity: 0.1,
      weight: 1,
    }).addTo(map);

    markerRef.current = L.marker([latitude, longitude]).addTo(map);

    const markerElement = markerRef.current.getElement();
    if (markerElement) {
      markerElement.style.filter =
        "hue-rotate(150deg) brightness(0.9) drop-shadow(0 2px 4px rgba(0,0,0,0.3))";
    }

    const timeoutId = window.setTimeout(() => {
      map.invalidateSize();
      map.setView([latitude, longitude], 17);
    }, 100);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [startCoordinates]);

  useEffect(() => {
    if (!isVisible || !startCoordinates || !mapInstanceRef.current) {
      return;
    }

    const { latitude, longitude } = startCoordinates;
    const timeoutId = window.setTimeout(() => {
      mapInstanceRef.current?.invalidateSize();
      mapInstanceRef.current?.setView([latitude, longitude], 17);
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [isVisible, startCoordinates]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markerRef.current = null;
      circleRef.current = null;
      zoomControlsRef.current = null;
    };
  }, []);

  const handleStartVisit = () => {
    if (isLocked || isLocallyStarting || isStarting) return;

    const confirmed = window.confirm(
      `Deseja iniciar a visita para o cliente "${selectedClient?.apelido || selectedClient?.nome}"?\n\nApós iniciar, não será possível alterar o cliente.`
    );
    if (!confirmed) return;

    setIsLocallyStarting(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const accuracy = position.coords.accuracy;

          if (accuracy > BAD_ACCURACY_THRESHOLD) {
            alert(
              `Sinal de GPS fraco (Precisão: ${Math.round(
                accuracy
              )}m). Por favor, saia para um local aberto e tente novamente.`
            );
            setIsLocallyStarting(false);
            return;
          }

          try {
            await onStartVisit(position.coords);
          } finally {
            setIsLocallyStarting(false);
          }
        },
        (error) => {
          setIsLocallyStarting(false);
          console.error("Erro ao obter geolocalização:", error);
          alert(
            "Erro ao obter localização. Verifique as permissões do navegador."
          );
        }
      );
    } else {
      setIsLocallyStarting(false);
      alert("Geolocalização não é suportada por este navegador.");
    }
  };

  const isStartDisabled = !selectedClient || isStarting || isLocked;
  const showMap = !!startCoordinates;

  return (
    <Grid item xs={12} md={12}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  backgroundColor: Colors.titleRowWithBackgroundColor,
                  pl: 1,
                  pt: 0.75,
                  mt: -1,
                  ml: -2,
                  mr: -2,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                variant="h6"
                color={Colors.titleWithBackgroudColor}
              >
                <PersonOutline fontSize="inherit" /> Check-in
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardActionArea
                  disabled={isLocked || isStarting || isLocallyStarting}
                  onClick={
                    isLocked || isStarting || isLocallyStarting
                      ? undefined
                      : toggleModal
                  }
                  sx={{ p: 2 }}
                >
                  {selectedClient ? (
                    <Grid container direction="column">
                      <Typography variant="h6">
                        {selectedClient.apelido || selectedClient.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedClient.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedClient.id}
                      </Typography>
                    </Grid>
                  ) : (
                    <Typography
                      variant="body1"
                      color="primary"
                      textAlign="center"
                    >
                      Toque para selecionar o cliente
                    </Typography>
                  )}
                </CardActionArea>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Data"
                value={checkinDate}
                disabled
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <EventAvailable color="action" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hora"
                value={checkinTime}
                disabled
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <AccessTime color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>

            {!showMap ? (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  disabled={isStartDisabled || isLocked || isLocallyStarting}
                  onClick={handleStartVisit}
                  startIcon={<LocationOn />}
                  sx={{ mt: 1, py: 1.5 }}
                >
                  {isStarting || isLocallyStarting
                    ? "Iniciando..."
                    : "Iniciar Visita"}
                </Button>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="success.main"
                  textAlign="center"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  Visita Iniciada com Sucesso
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    mt: 2,
                    height: "350px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #ddd",
                  }}
                >
                  {mapLoading && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        zIndex: 10,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}

                  <Box
                    ref={mapContainerRef}
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default VisitCheckinCard;
